import { useEffect, useMemo, useState } from "react";
import { X, Copy, Check, MessageCircle, User, Phone, Languages, FileText, Type, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { copyToClipboard, buildWhatsAppApiUrl } from "@/lib/whatsapp";
import { LANG_META, LANGS, buildReceiptText, currentUiLang } from "@/lib/receipt";

// Kısa PDF eşlik mesajı — dil-farkında (Full text modu → tam fiş; PDF modu → kısa özet)
const PDF_CAPTION = {
  tr: (shop) => `Merhaba, *${shop}* işlem faturanız ekte bulunmaktadır. Bizi tercih ettiğiniz için teşekkür ederiz.\n🌐 www.milyemsoft.com`,
  en: (shop) => `Hello, please find your *${shop}* transaction invoice attached. Thank you for choosing us.\n🌐 www.milyemsoft.com`,
  ar: (shop) => `مرحباً، تجدون إيصال معاملتكم من *${shop}* في المرفق. شكراً لاختياركم لنا.\n🌐 www.milyemsoft.com`,
  de: (shop) => `Hallo, im Anhang finden Sie Ihren Transaktionsbeleg von *${shop}*. Vielen Dank für Ihren Besuch.\n🌐 www.milyemsoft.com`,
  ru: (shop) => `Здравствуйте! Ваш чек операции *${shop}* во вложении. Спасибо, что выбрали нас.\n🌐 www.milyemsoft.com`,
};

const MODE_LABEL = {
  text: { tr:"Yazı",   en:"Text",   ar:"نص",     de:"Text",     ru:"Текст" },
  pdf:  { tr:"PDF",    en:"PDF",    ar:"PDF",    de:"PDF",      ru:"PDF" },
};

/**
 * WhatsApp Fişi Gönder — Garantili Modal + Dil Seçimi.
 *
 * Neden buton? Tarayıcı popup blocker'ı `window.open()`'ı sadece kullanıcı-tetikli
 * eylemlerde geçirir. Dükkan sahibinin doğrudan tıkladığı buton ASLA
 * engellenmez. Adımlar:
 *   1) Modal açılır → varsayılan olarak UI dilinde önizleme
 *   2) Bayrak menüsü → müşteri diline göre metin saniyeler içinde yeniden üretilir
 *   3) "Mesajı Kopyala" → panoya kopyalar
 *   4) "WhatsApp'ı Aç" → api.whatsapp.com/send yeni sekmede — mesaj otomatik dolar
 *
 * Data-testids: wa-modal, wa-modal-copy, wa-modal-open, wa-modal-close,
 *               wa-modal-lang-btn, wa-modal-lang-{code}
 */
export default function WhatsAppReceiptModal({ open, phone, customerName, lastTxn, shopName, shopMeta, fetchInvoicePdf, onClose }) {
  const [copied, setCopied] = useState(false);
  const [lang, setLang] = useState(currentUiLang());
  const [langOpen, setLangOpen] = useState(false);
  const [mode, setMode] = useState("text"); // "text" | "pdf"
  const [pdfBusy, setPdfBusy] = useState(false);

  const fullText = useMemo(
    () => (lastTxn ? buildReceiptText(lastTxn, shopName, shopMeta, lang) : ""),
    [lastTxn, shopName, shopMeta, lang]
  );
  const shortCaption = useMemo(() => (PDF_CAPTION[lang] || PDF_CAPTION.tr)(shopName || ""), [lang, shopName]);
  const text = mode === "pdf" ? shortCaption : fullText;

  const cleanedPhone = useMemo(() => String(phone || "").replace(/\D/g, ""), [phone]);
  const waUrl = useMemo(() => buildWhatsAppApiUrl(cleanedPhone, text || ""), [cleanedPhone, text]);

  useEffect(() => {
    if (!open) { setCopied(false); setLangOpen(false); setLang(currentUiLang()); return; }
    // Otomatik: modal açılır açılmaz mesajı panoya kopyala — sahibi "Kopyala"ya
    // basmasa bile "WhatsApp'ı Aç"tan sonra tek Ctrl+V yeter.
    (async () => {
      const ok = await copyToClipboard(text || "");
      setCopied(ok);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Dil veya mod değişince yeni metni tekrar panoya kopyala
  useEffect(() => {
    if (!open) return;
    (async () => {
      const ok = await copyToClipboard(text || "");
      setCopied(ok);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, mode]);

  const onCopy = async () => {
    const ok = await copyToClipboard(text || "");
    setCopied(ok);
    if (ok) toast.success("Mesaj panoya kopyalandı");
    else toast.error("Kopyalama başarısız — mesajı elle seç ve kopyala");
  };

  const onOpen = () => {
    // Kullanıcı-tetikli açılış → popup blocker ASLA engellemez.
    window.open(waUrl, "_blank", "noopener,noreferrer");
    toast.success("WhatsApp yeni sekmede açıldı", {
      description: "Mesaj otomatik gelmezse sohbette Ctrl+V ile yapıştır.",
    });
  };

  const onSendPdf = async () => {
    if (!fetchInvoicePdf || !lastTxn?.id) return toast.error("Fatura bulunamadı");
    setPdfBusy(true);
    try {
      const blob = await fetchInvoicePdf(lastTxn.id);
      const fname = `MilyemSoft-Fatura-${(lastTxn.id || "").slice(0,8).toUpperCase()}.pdf`;

      // 1) Mobil: Web Share API + files → müşteriye direkt PDF paylaş
      try {
        const file = new File([blob], fname, { type: "application/pdf" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: fname, text: shortCaption });
          toast.success("Paylaşım penceresi açıldı");
          setPdfBusy(false);
          return;
        }
      } catch (_e) { /* fall-through */ }

      // 2) Masaüstü: PDF'i dükkan sahibinin bilgisayarına indir + WhatsApp'ı aç
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 4000);

      // Kısa metni panoya + WA'yı aç
      await copyToClipboard(shortCaption);
      window.open(waUrl, "_blank", "noopener,noreferrer");
      toast.success("PDF indirildi, WhatsApp açıldı", {
        description: "WhatsApp'ta ataç ikonuna dokun ve indirilen PDF'i seç.",
      });
    } catch (e) {
      toast.error("PDF indirilemedi");
    } finally {
      setPdfBusy(false);
    }
  };

  if (!open) return null;

  const active = LANG_META[lang] || LANG_META.tr;
  const modeLabels = { text: MODE_LABEL.text[lang] || "Yazı", pdf: MODE_LABEL.pdf[lang] || "PDF" };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      data-testid="wa-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg card-flat border relative"
        style={{ borderColor: "rgba(37,211,102,0.45)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center rounded-full" style={{ background: "#25D366" }}>
              <MessageCircle size={16} className="text-black" />
            </div>
            <div>
              <div className="text-sm font-semibold">WhatsApp Fişi Gönder</div>
              <div className="label-caps text-[10px]" style={{ color: "#25D366" }}>Garantili Modal</div>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Kapat"
            data-testid="wa-modal-close"
            className="w-9 h-9 border border-white/15 flex items-center justify-center hover:border-red-400 hover:text-red-300 transition"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-4 sm:px-5 py-4 space-y-3">
          <div className="flex items-center gap-3 pb-3 border-b border-white/5">
            <div className="w-9 h-9 flex items-center justify-center border border-white/15 shrink-0">
              <User size={14} className="text-neutral-400" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm truncate">{customerName || "Müşteri"}</div>
              <div className="text-[11px] text-neutral-500 font-mono tabular-nums flex items-center gap-1">
                <Phone size={10} />
                +{cleanedPhone}
              </div>
            </div>

            {/* Dil seçici — turist müşteriye kendi dilinde fiş */}
            <div className="relative shrink-0">
              <button
                type="button"
                onClick={() => setLangOpen((v) => !v)}
                data-testid="wa-modal-lang-btn"
                className="flex items-center gap-1.5 border border-white/15 px-2.5 py-2 text-xs hover:border-[--gold] hover:text-[--gold] transition"
                title="Fiş dilini değiştir"
              >
                <Languages size={12} />
                <span>{active.flag}</span>
                <span className="tracking-widest">{active.short}</span>
              </button>
              {langOpen && (
                <>
                  <div className="fixed inset-0 z-[5]" onClick={() => setLangOpen(false)} />
                  <div className="absolute right-0 top-full mt-1 min-w-[160px] border border-white/15 bg-[#0a0a0a] z-10 shadow-2xl">
                    {LANGS.map((code) => {
                      const m = LANG_META[code];
                      const sel = code === lang;
                      return (
                        <button
                          key={code}
                          type="button"
                          onClick={() => { setLang(code); setLangOpen(false); }}
                          data-testid={`wa-modal-lang-${code}`}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-xs transition ${sel ? "text-[--gold] bg-white/5" : "hover:bg-white/5"}`}
                          style={sel ? { color: "var(--gold)" } : undefined}
                        >
                          <span>{m.flag}</span>
                          <span className="flex-1 text-left">{m.label}</span>
                          {sel && <Check size={12} />}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Gönderim Tipi Toggle: Yazı / PDF */}
          <div className="grid grid-cols-2 gap-0 border border-white/10 overflow-hidden" data-testid="wa-mode-toggle">
            <button
              type="button"
              onClick={() => setMode("text")}
              data-testid="wa-mode-text"
              className={`flex items-center justify-center gap-1.5 py-2 text-xs transition ${mode==="text" ? "text-black font-semibold" : "text-neutral-400 hover:text-white"}`}
              style={mode==="text" ? { background: "var(--gold)" } : undefined}
            >
              <Type size={12} /> {modeLabels.text}
            </button>
            <button
              type="button"
              onClick={() => setMode("pdf")}
              data-testid="wa-mode-pdf"
              className={`flex items-center justify-center gap-1.5 py-2 text-xs transition ${mode==="pdf" ? "text-black font-semibold" : "text-neutral-400 hover:text-white"}`}
              style={mode==="pdf" ? { background: "var(--gold)" } : undefined}
            >
              <FileText size={12} /> {modeLabels.pdf}
            </button>
          </div>

          <div>
            <div className="label-caps text-[10px] mb-1.5 flex items-center justify-between">
              <span>Mesaj Önizlemesi</span>
              {mode === "pdf" && <span className="normal-case tracking-normal text-neutral-500">PDF eşliğinde giden kısa metin</span>}
            </div>
            <textarea
              readOnly
              value={text || ""}
              rows={7}
              className="w-full bg-black/40 border border-white/10 px-3 py-2 text-[11px] font-mono leading-relaxed text-neutral-300 focus:outline-none focus:border-[--gold] resize-none"
              data-testid="wa-modal-preview"
            />
            <div className="text-[10px] text-neutral-500 mt-1.5">
              {copied ? "✓ Mesaj panonda hazır — sohbete Ctrl+V ile yapıştırabilirsin." : "Mesaj panoya kopyalanıyor…"}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 px-4 sm:px-5 pb-4">
          <button
            onClick={onCopy}
            data-testid="wa-modal-copy"
            className={`flex items-center justify-center gap-2 border px-3 py-3 text-sm transition active:scale-95 ${copied ? "border-emerald-400 text-emerald-300" : "border-white/15 hover:border-[--gold] hover:text-[--gold]"}`}
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? "Kopyalandı" : "Mesajı Kopyala"}
          </button>
          {mode === "text" ? (
            <button
              onClick={onOpen}
              data-testid="wa-modal-open"
              className="flex items-center justify-center gap-2 px-3 py-3 text-sm text-black font-semibold transition active:scale-95 hover:brightness-110"
              style={{ background: "#25D366" }}
            >
              <MessageCircle size={14} />
              WhatsApp'ı Aç
            </button>
          ) : (
            <button
              onClick={onSendPdf}
              disabled={pdfBusy}
              data-testid="wa-modal-send-pdf"
              className="flex items-center justify-center gap-2 px-3 py-3 text-sm text-black font-semibold transition active:scale-95 hover:brightness-110 disabled:opacity-60 disabled:cursor-wait"
              style={{ background: "#25D366" }}
            >
              {pdfBusy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              PDF + WhatsApp
            </button>
          )}
        </div>

        <div className="px-4 sm:px-5 pb-4 text-[10px] text-neutral-500 tracking-wide">
          {mode === "text" ? (
            <><strong style={{ color: "#25D366" }}>3 saniye</strong> · Kopyala → Aç → Yapıştır (Ctrl+V)</>
          ) : (
            <><strong style={{ color: "var(--gold)" }}>PDF akışı</strong> · PDF indir + WhatsApp'ta ataç ikonundan yükle</>
          )}
        </div>
      </div>
    </div>
  );
}
